import React, {useEffect, useRef, useState} from "react";

const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAP_API_KEY || "";

export function CircleTestPage() {
    const mapDivRef = useRef<HTMLDivElement>(null);
    const mapRef = useRef<google.maps.Map | null>(null);
    const polygonRef = useRef<google.maps.Polygon | null>(null);
    const referenceCircleRef = useRef<google.maps.Circle | null>(null);
    const [googleLoaded, setGoogleLoaded] = useState(false);
    const [centerLat, setCenterLat] = useState(0);
    const [centerLng, setCenterLng] = useState(0);
    const [radiusKm, setRadiusKm] = useState(1000);
    const [showReference, setShowReference] = useState(true);

    // Load Google Maps
    useEffect(() => {
        if (window.google?.maps) {
            setGoogleLoaded(true);
            return;
        }

        const script = document.createElement("script");
        script.src = `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_MAPS_API_KEY}&v=3.64&libraries=geometry`;
        script.async = true;
        script.onload = () => setGoogleLoaded(true);
        document.head.appendChild(script);
    }, []);

    // Initialize Google Map
    useEffect(() => {
        if (!googleLoaded || !mapDivRef.current || mapRef.current) return;

        const map = new google.maps.Map(mapDivRef.current, {
            center: {lat: centerLat, lng: centerLng},
            zoom: 2,
            mapTypeId: "terrain"
        });

        mapRef.current = map;
    }, [googleLoaded]);

    // Update circle
    useEffect(() => {
        const map = mapRef.current;
        if (!map) return;

        map.setCenter({lat: centerLat, lng: centerLng});

        const radiusMeters = radiusKm * 1000;

        // Generate circle points using Google's own spherical geometry library
        const segments = 128;
        const points: google.maps.LatLngLiteral[] = [];
        const center = new google.maps.LatLng(centerLat, centerLng);

        // Generate points with normal bearing order first
        for (let i = 0; i <= segments; i++) {
            const bearing = (i / segments) * 360;
            const point = google.maps.geometry.spherical.computeOffset(center, radiusMeters, bearing);
            points.push({lat: point.lat(), lng: point.lng()});
        }

        // Now check the signed area and reverse if needed
        const latLngPath = points.map(p => new google.maps.LatLng(p.lat, p.lng));
        const signedArea = google.maps.geometry.spherical.computeSignedArea(latLngPath);

        console.log("Signed area BEFORE any reversal:", (signedArea / 1e12).toFixed(2), "trillion m²");

        // Positive signed area = counterclockwise = correct winding for small areas
        // Negative signed area = clockwise = draws the complement (the outside)
        // We want positive (counterclockwise) so if negative, reverse the array
        if (signedArea < 0) {
            console.log("Reversing array because signed area is negative");
            points.reverse();
        } else {
            console.log("NOT reversing - signed area is already positive");
        }

        // Debug: log first few points to see what we're getting
        if (centerLat < 0 && radiusKm > 2000) {
            console.log("South pole test - Center:", centerLat, centerLng);
            console.log("First 4 points:", points.slice(0, 4));
            console.log("Points at 90° intervals:", [points[0], points[32], points[64], points[96]]);
        }

        // Check if any points are very close to or past a pole
        const minLat = Math.min(...points.map(p => p.lat));
        const maxLat = Math.max(...points.map(p => p.lat));
        const spansPole = minLat <= -85 || maxLat >= 85;

        // Reverse polygon only if not near poles
        if (!spansPole && signedArea < 0) {
            console.log("Reversing array for normal area");
            points.reverse();
        } else if (spansPole) {
            console.log("Polygon spans a pole, skipping reversal");
        }

        console.log("Min lat:", minLat, "Max lat:", maxLat, "Spans pole?", spansPole);

        // Remove old polygon
        if (polygonRef.current) {
            polygonRef.current.setMap(null);
        }

        let polygon: google.maps.Polygon;

        if (spansPole) {
            // For circles near poles, use geodesic mode which handles wrapping better
            polygon = new google.maps.Polygon({
                map: map,
                paths: points,
                geodesic: true, // This is the key - tells Google Maps to draw great circle arcs
                strokeColor: "#0000FF",
                strokeOpacity: 0.8,
                strokeWeight: 2,
                fillColor: "#0000FF",
                fillOpacity: 0.2
            });
        } else {
            // Normal polygon for circles away from poles
            polygon = new google.maps.Polygon({
                map: map,
                paths: points,
                strokeColor: "#0000FF",
                strokeOpacity: 0.8,
                strokeWeight: 2,
                fillColor: "#0000FF",
                fillOpacity: 0.2
            });
        }

        polygonRef.current = polygon;

        // Update reference circle
        if (referenceCircleRef.current) {
            referenceCircleRef.current.setMap(null);
        }

        if (showReference) {
            // Add Google's reference circle
            const referenceCircle = new google.maps.Circle({
                map: map,
                center: {lat: centerLat, lng: centerLng},
                radius: radiusMeters,
                strokeColor: "#FF0000",
                strokeOpacity: 0.5,
                strokeWeight: 1,
                fillColor: "#FF0000",
                fillOpacity: 0.1
            });

            referenceCircleRef.current = referenceCircle;
        }

    }, [centerLat, centerLng, radiusKm, showReference, googleLoaded]);

    return (
        <div className="h-screen flex flex-col pt-36">
            <div className="bg-white border-b border-light-grey p-4">
                <h1 className="text-2xl font-bold">Geographic Circle Math Test</h1>
                <p className="text-sm text-grey mt-1">
                    Blue = Our calculation, Red = Google Maps reference
                </p>
            </div>

            <div className="flex-1 flex overflow-hidden">
                <div className="w-80 bg-white border-r border-light-grey p-4 overflow-y-auto">
                    <h2 className="font-semibold mb-3">Circle Parameters</h2>

                    <div className="mb-4">
                        <label className="block text-sm font-medium mb-2">
                            Center Latitude: {centerLat.toFixed(2)}°
                        </label>
                        <input
                            type="range"
                            min="-90"
                            max="90"
                            step="1"
                            value={centerLat}
                            onChange={(e) => setCenterLat(parseFloat(e.target.value))}
                            className="w-full"
                        />
                    </div>

                    <div className="mb-4">
                        <label className="block text-sm font-medium mb-2">
                            Center Longitude: {centerLng.toFixed(2)}°
                        </label>
                        <input
                            type="range"
                            min="-180"
                            max="180"
                            step="1"
                            value={centerLng}
                            onChange={(e) => setCenterLng(parseFloat(e.target.value))}
                            className="w-full"
                        />
                    </div>

                    <div className="mb-4">
                        <label className="block text-sm font-medium mb-2">
                            Radius: {radiusKm}km
                        </label>
                        <input
                            type="range"
                            min="100"
                            max="10000"
                            step="100"
                            value={radiusKm}
                            onChange={(e) => setRadiusKm(parseFloat(e.target.value))}
                            className="w-full"
                        />
                    </div>

                    <div className="mb-4">
                        <label className="flex items-center">
                            <input
                                type="checkbox"
                                checked={showReference}
                                onChange={(e) => setShowReference(e.target.checked)}
                                className="mr-2"
                            />
                            <span className="text-sm">Show Google Maps reference circle</span>
                        </label>
                    </div>

                    <div className="text-sm text-grey mt-6">
                        <p className="font-medium mb-2">Test cases:</p>
                        <button
                            onClick={() => {
                                setCenterLat(80);
                                setCenterLng(0);
                                setRadiusKm(2000);
                            }}
                            className="w-full text-left px-3 py-2 mb-2 bg-gray-100 hover:bg-gray-200 rounded"
                        >
                            Near North Pole
                            <div className="text-xs text-grey">lat=80°, lng=0°, r=2000km</div>
                        </button>
                        <button
                            onClick={() => {
                                setCenterLat(0);
                                setCenterLng(170);
                                setRadiusKm(2000);
                            }}
                            className="w-full text-left px-3 py-2 mb-2 bg-gray-100 hover:bg-gray-200 rounded"
                        >
                            Crosses Date Line
                            <div className="text-xs text-grey">lat=0°, lng=170°, r=2000km</div>
                        </button>
                        <button
                            onClick={() => {
                                setCenterLat(0);
                                setCenterLng(0);
                                setRadiusKm(5000);
                            }}
                            className="w-full text-left px-3 py-2 mb-2 bg-gray-100 hover:bg-gray-200 rounded"
                        >
                            Near Equator (Large)
                            <div className="text-xs text-grey">lat=0°, lng=0°, r=5000km</div>
                        </button>
                        <button
                            onClick={() => {
                                setCenterLat(-80);
                                setCenterLng(0);
                                setRadiusKm(2000);
                            }}
                            className="w-full text-left px-3 py-2 mb-2 bg-gray-100 hover:bg-gray-200 rounded"
                        >
                            Near South Pole
                            <div className="text-xs text-grey">lat=-80°, lng=0°, r=2000km</div>
                        </button>
                    </div>
                </div>

                <div className="flex-1">
                    <div ref={mapDivRef} className="w-full h-full"/>
                </div>
            </div>
        </div>
    );
}
